import { useState } from 'react';
import { NJBadge, NJButton, NJHeading, NJIcon, NJInputSearch, NJText } from '@engie-group/fluid-design-system-react';
import { CURRENT_USER } from './homeModel';
import { useProject, useReferential } from './hooks';
import { ProjectMiniMap } from './ProjectMiniMap';
import { DevelopmentPlansTable } from './DevelopmentPlansTable';
import { ProjectAccessAvatars } from './ProjectAccessAvatars';
import type { ProjectSummary } from './types';
import './wireframes.css';

function WireframeCard({ project, onOpen, onDuplicate, flash }: {
  project: ProjectSummary;
  onOpen: (id: string) => void;
  onDuplicate: (id: string) => void;
  flash: (message: string) => void;
}) {
  const detail = useProject(project.id);
  const referential = useReferential();
  const month = (value: string) => value.split('-').reverse().join('/');

  return (
    <article className="pcv3-wf-card" aria-label={project.name} data-comment-anchor={`wireframe-card-${project.id}`}>
      <header className="pcv3-wf-card-header">
        <div className="pcv3-wf-heading">
          <NJHeading as="h3" scale="sm">
            <a className="nj-link nj-link--contextual" href="#" onClick={(event) => {
              event.preventDefault();
              onOpen(project.id);
            }}>{project.name}</a>
          </NJHeading>
          <NJText scale="sm" className="pcv3-project-description">{project.description || 'Aucune description renseignée.'}</NJText>
          <div className="pcv3-project-metadata">
            <NJText scale="sm">Créateur : <strong>{project.owner}</strong> · {project.owner === CURRENT_USER ? 'Créé par moi' : 'Partagé avec moi'}</NJText>
            <NJText scale="sm">Dernière modification : {project.updatedAt}</NJText>
          </div>
        </div>
        <div className="pcv3-wf-header-actions">
          <ProjectAccessAvatars project={project} />
          <NJBadge variant="information" emphasis="subtle" scale="sm">En cours</NJBadge>
          <NJButton label="Dupliquer" icon="content_copy" variant="secondary" scale="sm" onClick={() => onDuplicate(project.id)} />
        </div>
      </header>
      <div className="pcv3-wf-card-body">
        <div className="pcv3-mini-map-slot">
          {detail.data && referential.data ? <ProjectMiniMap detail={detail.data} referential={referential.data} /> : (
            <div className="pcv3-wf-map pcv3-map-message">
              <NJText role={detail.isError || referential.isError ? 'alert' : 'status'}>
                {detail.isError || referential.isError ? 'Impossible de charger la carte du projet.' : 'Chargement de la carte…'}
              </NJText>
            </div>
          )}
        </div>
        <div className="pcv3-wf-facts">
          <div>
            <NJText as="div" scale="sm">Périmètre / commune(s)</NJText>
            <NJText as="strong">{project.communeNames.join(', ')}</NJText>
            {project.perimeterLabel === 'Zone dessinée' && <NJText scale="sm">Zone dessinée dans la commune</NJText>}
          </div>
          <div className="pcv3-wf-tag">
            <div className="pcv3-thermometer"><NJIcon name="thermostat" scale="sm" variant="inherit" aria-hidden="true" /></div>
            <NJText as="span" scale="sm" role={detail.isError ? 'alert' : undefined}>
              {detail.isError ? 'DJU : chargement impossible' : detail.data
                ? `DJU de référence : ${detail.data.project.dju.reference.toLocaleString('fr-FR')}`
                : 'DJU : chargement…'}
            </NJText>
          </div>
          <div>
            <NJText as="div" scale="sm">Type de projet</NJText>
            <NJText>{project.kind === 'new' ? 'Nouveau réseau' : 'Réseau existant'}</NJText>
          </div>
          <div>
            <NJText as="div" scale="sm">Période du contrat</NJText>
            <NJText>{month(project.contract.start)} → {month(project.contract.end)}</NJText>
          </div>
        </div>
        <DevelopmentPlansTable project={project} flash={flash} />
      </div>
    </article>
  );
}

export function ProjectCardWireframes({ projects, onCreate, onOpen, onDuplicate, flash }: {
  projects: ProjectSummary[];
  onCreate: () => void;
  onOpen: (id: string) => void;
  onDuplicate: (id: string) => void;
  flash: (message: string) => void;
}) {
  const [search, setSearch] = useState('');
  const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('fr-FR');
  const examples = projects.filter((project) => !project.archived && !project.completed
    && (project.owner === CURRENT_USER || project.members.includes(CURRENT_USER)));
  const byId = new Map(examples.map((project) => [project.id, project]));
  const families = examples.filter((project) => !project.parentId || !byId.has(project.parentId)).map((root) => ({
    root, projects: [root, ...examples.filter((project) => project.parentId === root.id)],
  })).map((family) => ({
    ...family,
    matches: family.projects.filter((project) => normalize([
      project.name, project.description ?? '', ...project.communeNames, project.owner, ...project.members,
    ].join(' ')).includes(normalize(search.trim()))),
  })).filter((family) => family.matches.length > 0);
  const count = families.reduce((total, family) => total + family.matches.length, 0);

  return (
    <section className="pcv3-wireframes">
      <div className="pcv3-section-heading">
        <NJHeading as="h2" scale="md">Mes projets en cours ({count})</NJHeading>
        <div className="pcv3-project-search" onClick={(event) => {
          if (event.target instanceof Element && event.target.closest('button')) setSearch('');
        }}>
          <NJInputSearch id="pcv3-current-search" label="Rechercher dans les projets en cours"
            aria-label="Rechercher dans les projets en cours" placeholder="Nom, commune, personne…"
            value={search} onChange={(event) => setSearch(event.target.value)} resetButtonTitle="Effacer la recherche" />
        </div>
        <NJButton label="Nouveau projet" icon="add" onClick={onCreate} />
      </div>
      {count === 0 && <NJText role="status">Aucun projet en cours ne correspond à la recherche.</NJText>}
      {families.map((family) => (
        <section key={family.root.id} className="pcv3-project-family" aria-label={`Famille de ${family.root.name}`}>
          {family.projects.length > 1 && <NJHeading as="h3" scale="sm">Famille : {family.root.name}</NJHeading>}
          <div className="pcv3-wf-projects pcv3-wf-grid">
            {family.matches.map((project) => <WireframeCard key={project.id} project={project} onOpen={onOpen} onDuplicate={onDuplicate} flash={flash} />)}
          </div>
        </section>
      ))}
    </section>
  );
}
